import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  SlDrawer,
  SlButton,
  SlIcon,
  SlInput,
  SlTooltip,
  SlDialog,
  SlSelect,
  SlOption,
  SlBadge,
} from "@shoelace-style/shoelace/dist/react";
import ScrollContainer from "react-indiana-drag-scroll";
import api from "../../services/api";
import { Toast } from "../../shared/Toast";
import TableLoader from "../../shared/TableLoader";
import { formatDate, formatDateWithTimeYear } from "../../utils/dateFormat";
import "./GenerateInvoices.scss";

const PAGE_SIZE = 500;

// POST /billing/v1/invoices request body, in the order Zoho documents it.
/**
 * Column visibility is remembered per view.
 *
 * What is stored is the HIDDEN set, not the visible one: a column added to the
 * grid later is then shown by default rather than silently missing for everyone
 * who has ever opened the picker.
 */
const COLUMN_PREFS_KEY = "zoho_invoice_hidden_columns";

const loadHiddenColumns = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(COLUMN_PREFS_KEY) || "{}");
    return {
      candidates: Array.isArray(saved.candidates) ? saved.candidates : [],
      raised: Array.isArray(saved.raised) ? saved.raised : [],
    };
  } catch (error) {
    // A corrupt entry must not take the drawer down with it.
    return { candidates: [], raised: [] };
  }
};

/** Ids are prefixed, so the same key in two groups stays distinct. */
const withIds = (group, columns) =>
  columns.map((col) => ({ ...col, id: `${group}.${col.key}` }));

const ZOHO_COLUMNS = withIds("zoho", [
  { label: "customer_id", key: "customer_id", empty: "unmapped" },
  { label: "email", key: "email" },
  { label: "date", key: "date", edit: true },
  { label: "invoice_number", key: "invoice_number", empty: "auto" },
  { label: "place_of_supply", key: "place_of_supply" },
  { label: "gst_treatment", key: "gst_treatment" },
  { label: "gst_no", key: "gst_no" },
  { label: "payment_terms", key: "payment_terms" },
  { label: "exchange_rate", key: "exchange_rate" },
  { label: "is_inclusive_tax", key: "is_inclusive_tax", render: (v) => String(!!v) },
  { label: "salesperson_name", key: "salesperson_name" },
  { label: "sub_total", key: "sub_total" },
  { label: "tax_amount", key: "tax_amount" },
  { label: "total_amount", key: "total_amount" },
]);

// invoice_items[] - one sub-row per line item.
const ITEM_COLUMNS = withIds("item", [
  { label: "product_id", key: "product_id", empty: "unmapped" },
  { label: "name", key: "name" },
  { label: "description", key: "description" },
  { label: "quantity", key: "quantity" },
  { label: "rate", key: "rate", edit: true },
  { label: "tax_name", key: "tax_name" },
  { label: "tax_percentage", key: "tax_percentage" },
]);

// Panel-side context, so finance can sanity-check what Zoho is about to get.
const PANEL_COLUMNS = withIds("panel", [
  { label: "Order Name", key: "order_name" },
  // Kept beside the order it names. The invoice carries it as the "Order No"
  // custom field, but this column is the panel's own handle on the row.
  { label: "Order ID", key: "order_id" },
  // The name the invoice is billed to, editable in the row: it is the field
  // most often wrong and the one that decides which Zoho customer is used.
  { label: "Customer", key: "customer_name", edit: true },
  // The three sources it falls back through, in that order. Display only - the
  // request stores the resolved customer_name, not these.
  { label: "Brand", key: "brand_name" },
  { label: "Client", key: "client_name" },
  { label: "Company", key: "company_name" },
  { label: "Order Value", key: "order_value" },
  { label: "Spent", key: "spent" },
  {
    label: "Idle",
    key: "days_since_end",
    render: (v) => (v || v === 0 ? `${v}d` : "-"),
  },
  { label: "Incl. GST", key: "order_includes_gst", render: (v) => (v ? "Yes" : "No") },
  { label: "Campaign Ended", key: "o_end_date", render: (v) => (v ? formatDate(v) : "-") },
]);

// zoho_invoice_requests.status
const REQUEST_STATUS = {
  0: { label: "Requested", variant: "neutral" },
  1: { label: "Pushed", variant: "success" },
  2: { label: "Failed", variant: "danger" },
  3: { label: "Cancelled", variant: "warning" },
};

// The requests already raised. `date` is a DATE column, so it arrives as an ISO
// string rather than the unix seconds the panel uses everywhere else.
const RAISED_COLUMNS = withIds("raised", [
  { label: "Invoice No", key: "invoice_number", empty: "not numbered" },
  { label: "Order", key: "order_id" },
  { label: "Customer", key: "customer_name" },
  { label: "Invoice Date", key: "date", render: (v) => String(v).slice(0, 10) },
  { label: "Taxable", key: "sub_total" },
  { label: "GST", key: "tax_amount" },
  { label: "Total", key: "total_amount" },
  { label: "Salesperson", key: "salesperson_name" },
  { label: "Raised By", key: "requested_by_name" },
  { label: "Raised On", key: "created_at", render: (v) => formatDateWithTimeYear(v) },
  { label: "Pushed On", key: "pushed_at", render: (v) => formatDateWithTimeYear(v) },
  { label: "Zoho Invoice ID", key: "zoho_invoice_id", empty: "not pushed" },
  {
    label: "Emailed",
    key: "emailed_at",
    empty: "not sent",
    render: (v) => formatDateWithTimeYear(v),
  },
]);

// The picker's sections, so each group can be toggled on its own.
const COLUMN_GROUPS = {
  candidates: [
    { title: "Order context", columns: PANEL_COLUMNS },
    { title: "Invoice", columns: ZOHO_COLUMNS },
    { title: "Line item", columns: ITEM_COLUMNS },
  ],
  raised: [{ title: "Raised invoices", columns: RAISED_COLUMNS }],
};

// What finance may correct before raising. Mirrors EDITABLE_FIELDS on the API;
// the server re-applies and re-validates these, so this list is the form, not
// the rule. customer_name is not here: it keys the Zoho customer lookup, so a
// wrong one is fixed on the brand or company record, not on the invoice.
const EDIT_FIELDS = [
  // Editing this re-points the invoice at a different Zoho customer, or has
  // one created, since Zoho matches on display_name. The Brand/Client/Company
  // columns in the grid show where the derived name came from.
  {
    key: "customer_name",
    label: "Customer name",
    path: ["customer_name"],
    hint: "Changing this bills a different Zoho customer, or creates one",
  },
  { key: "email", label: "Email", path: ["email"], type: "email" },
  { key: "date", label: "Invoice date", path: ["date"], type: "date" },
  { key: "place_of_supply", label: "Place of supply", path: ["place_of_supply"], options: "states" },
  { key: "gst_treatment", label: "GST treatment", path: ["gst_treatment"], options: "gst_treatments" },
  { key: "gst_no", label: "GSTIN", path: ["gst_no"] },
  { key: "salesperson_name", label: "Salesperson", path: ["salesperson_name"] },
  { key: "street", label: "Billing address", path: ["billing_address", "street"] },
  { key: "city", label: "Billing city", path: ["billing_address", "city"] },
  { key: "item_name", label: "Product", path: ["invoice_items", 0, "name"], options: "products" },
  { key: "description", label: "Description", path: ["invoice_items", 0, "description"] },
  { key: "rate", label: "Amount (excl. GST)", path: ["invoice_items", 0, "rate"], type: "number" },
];

// Mirrors usableEmail() on the API. Kept in step deliberately: a row this
// accepts must be one the server accepts too, or Generate would skip a row the
// drawer showed as ready.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
const isUsableEmail = (value) => EMAIL_RE.test(String(value || "").trim());

/** A row Zoho would reject for its address, whatever else is right about it. */
const emailProblem = (row) => {
  const email = String(row?.email || "").trim();
  if (!email) return "No email on the client or company";
  if (!isUsableEmail(email)) return `"${email}" is not a valid email address`;
  return "";
};

const readPath = (row, path) =>
  path.reduce((value, step) => (value == null ? value : value[step]), row);

const cellValue = (row, col) => {
  const value = row[col.key];
  if (value === "" || value === null || value === undefined) return null;
  return col.render ? col.render(value) : String(value);
};

/**
 * CSV field: quoted only when it has to be, with embedded quotes doubled.
 *
 * A leading =, +, - or @ is prefixed with a quote so Excel reads the value as
 * text rather than running it as a formula - except when the whole field is a
 * plain number, which keeps amounts like -500 numeric instead of turning every
 * negative figure into text.
 */
const csvField = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  const formula = /^[=+\-@]/.test(text) && !/^-?\d+(\.\d+)?$/.test(text);
  const safe = formula ? `'${text}` : text;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

const toCsv = (headers, rows) =>
  [headers, ...rows].map((row) => row.map(csvField).join(",")).join("\r\n");

/**
 * Hands the browser a file. The BOM is what makes Excel open a UTF-8 CSV as
 * UTF-8 rather than mangling anything non-ASCII in a customer name.
 */
const downloadCsv = (filename, csv) => {
  const url = URL.createObjectURL(new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick: Safari has not finished with the URL at click.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const todayInput = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

function GenerateInvoices(props) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [noSpend, setNoSpend] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [loader, setLoader] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [endBefore, setEndBefore] = useState(todayInput());
  const [page, setPage] = useState(0);

  // "candidates" is what can still be invoiced, "raised" is what already was.
  const [view, setView] = useState("candidates");
  const [requests, setRequests] = useState([]);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("-1");
  const [requestsPage, setRequestsPage] = useState(0);
  const [pdf, setPdf] = useState(null); // { url, label }
  const [options, setOptions] = useState({ states: [], gst_treatments: [], products: [] });
  // Corrections finance has made, keyed by order_id. Sent with generate so the
  // server re-applies them; the row itself is replaced by the server's preview.
  const [overrides, setOverrides] = useState({});
  // The derived row as the server first produced it, kept per order so an edit
  // is always diffed against what was derived - never against a row that is
  // already carrying an earlier edit.
  const [baselines, setBaselines] = useState({});
  const [editing, setEditing] = useState(null); // { row, baseline, form }
  const [savingEdit, setSavingEdit] = useState(false);
  // "<order_id>:<field>" while an inline cell is being committed.
  const [savingCell, setSavingCell] = useState("");
  const [hiddenColumns, setHiddenColumns] = useState(loadHiddenColumns);
  const [pickingColumns, setPickingColumns] = useState(false);
  // Two values on purpose: `searchInput` is what the box shows, `search` is
  // what the API is asked for. Debouncing the second rather than the keystrokes
  // keeps the box responsive and issues one request per pause.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pdfLoading, setPdfLoading] = useState(0);
  const [exporting, setExporting] = useState(false);

  // Shoelace emits every event with bubbles/composed true, so a tooltip or a
  // nested overlay closing inside the drawer sends its own sl-after-hide up to
  // the drawer. Without this guard, hovering a validation icon closes the whole
  // drawer. Only react to events the drawer itself fired.
  const isOwnEvent = (event) => event.target === event.currentTarget;

  const handleRequestClose = (event) => {
    if (!isOwnEvent(event)) return;
    if (event.detail.source === "overlay") event.preventDefault();
  };

  const handleAfterHide = (event) => {
    if (!isOwnEvent(event)) return;
    props.setShow(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      // Both pages go back to the start: a term narrows the set, and an offset
      // from the old one would open past the end of the new.
      setPage(0);
      setRequestsPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchCandidates = useCallback(async () => {
    setLoader(true);
    try {
      const res = await api({
        url: "/zoho-invoice/candidates",
        method: "POST",
        responseType: "json",
        data: { end_before: endBefore, search, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
      });
      if (res.code === 200) {
        setRows(res.data?.data || []);
        setTotal(res.data?.total || 0);
        setNoSpend(res.data?.no_spend || 0);
        if (res.data?.options) setOptions(res.data.options);
        setSelected(new Set());
        setOverrides({});
        setBaselines({});
      } else {
        Toast("error", res.message || "Could not load invoice candidates");
      }
    } catch (error) {
      console.error(error);
      Toast("error", "Could not load invoice candidates");
    }
    setLoader(false);
  }, [endBefore, page, search]);

  const fetchRequests = useCallback(async () => {
    setLoader(true);
    try {
      const res = await api({
        url: "/zoho-invoice/requests",
        method: "POST",
        responseType: "json",
        data: {
          status: statusFilter === "-1" ? null : Number(statusFilter),
          search,
          limit: PAGE_SIZE,
          offset: requestsPage * PAGE_SIZE,
        },
      });
      if (res.code === 200) {
        setRequests(res.data?.data || []);
        setRequestsTotal(res.data?.total || 0);
      } else {
        Toast("error", res.message || "Could not load invoice requests");
      }
    } catch (error) {
      console.error(error);
      Toast("error", "Could not load invoice requests");
    }
    setLoader(false);
  }, [statusFilter, requestsPage, search]);

  useEffect(() => {
    if (!props.show) return;
    if (view === "raised") fetchRequests();
    else fetchCandidates();
  }, [props.show, view, fetchCandidates, fetchRequests]);

  /**
   * The stored PDF, shown in a dialog rather than a new tab: the route needs
   * the auth headers our axios instance adds, so the browser cannot open it as
   * a plain link, and a popup opened after an await tends to be blocked.
   */
  const openPdf = async (row) => {
    setPdfLoading(row.id);
    try {
      const res = await api({
        url: "/zoho-invoice/pdf",
        method: "POST",
        responseType: "blob",
        data: { id: row.id },
      });

      const label = row.invoice_number || row.order_id;

      // api() hands back the Blob on success but the whole response on a 4xx,
      // and two cases answer with JSON even though a blob was asked for: a row
      // with no PDF (404), and one whose file is on S3 - that one carries the
      // link, which is loaded straight into the viewer rather than fetched.
      const blob = res instanceof Blob ? res : res?.data;
      if (!(blob instanceof Blob) || blob.type === "application/json") {
        const body = blob instanceof Blob ? JSON.parse(await blob.text()) : res;
        const remote = body?.data?.url;
        if (remote) {
          // Not an object URL, so there is nothing to revoke for this one.
          setPdf((prev) => {
            if (prev?.url && prev.revoke) URL.revokeObjectURL(prev.url);
            return { url: remote, label, revoke: false };
          });
        } else {
          Toast("error", body?.message || "No PDF stored for this invoice yet");
        }
      } else {
        setPdf((prev) => {
          // Object URLs are held by the document until released by hand.
          if (prev?.url && prev.revoke) URL.revokeObjectURL(prev.url);
          return { url: URL.createObjectURL(blob), label, revoke: true };
        });
      }
    } catch (error) {
      console.error(error);
      Toast("error", "Could not open the invoice PDF");
    }
    setPdfLoading(0);
  };

  const closePdf = () => {
    setPdf((prev) => {
      if (prev?.url && prev.revoke) URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  // The drawer can be closed with a PDF still open.
  useEffect(() => () => setPdf((prev) => {
    if (prev?.url && prev.revoke) URL.revokeObjectURL(prev.url);
    return null;
  }), []);

  // Checked here as well as on the server so a stale row - one fetched before
  // the API was restarted, say - still cannot be ticked.
  // Checked here as well as on the server so a stale row - one fetched before
  // the API was restarted, say - still cannot be ticked.
  const validRows = useMemo(
    () => rows.filter((r) => !r.validation?.length && !emailProblem(r)),
    [rows]
  );
  const allValidSelected = validRows.length > 0 && validRows.every((r) => selected.has(r.order_id));

  const toggleRow = (orderId) => {
    const problem = emailProblem(rows.find((r) => r.order_id === orderId));
    if (problem) {
      Toast("error", problem);
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allValidSelected ? new Set() : new Set(validRows.map((r) => r.order_id)));
  };

  const openEdit = (row) => {
    // First time this row is opened it is still the derived one, so that is
    // the baseline. Afterwards the stored baseline is used - `row` by then
    // holds the previewed edit.
    const baseline = baselineFor(row);
    setEditing({
      row,
      baseline,
      // Seeded from the row on screen, so the dialog opens showing what the
      // grid shows.
      form: EDIT_FIELDS.reduce((form, field) => {
        const current = readPath(row, field.path);
        form[field.key] = current === null || current === undefined ? "" : String(current);
        return form;
      }, {}),
    });
  };

  // Live, so the address is corrected before Apply rather than bouncing back
  // off the server.
  const editEmailError = editing ? emailProblem({ email: editing.form.email }) : "";

  const setEditField = (key, value) =>
    setEditing((prev) => (prev ? { ...prev, form: { ...prev.form, [key]: value } } : prev));

  /**
   * Saves an edit by asking the server to re-derive the row with it applied,
   * rather than patching the row in the browser. Keeps one implementation of
   * the rules: the preview finance sees is the payload generate() will store.
   */
  /**
   * Sends a row's complete override set to the server and swaps in the row it
   * derives from them. Shared by the dialog and the inline customer-name cell,
   * so both go through the same rules and the same preview.
   */
  const commitOverrides = async (row, changed) => {
    const res = await api({
      url: "/zoho-invoice/preview",
      method: "POST",
      responseType: "json",
      data: { order_id: row.order_id, end_before: endBefore, overrides: changed },
    });
    if (res.code !== 200) {
      Toast("error", res.message || "Could not apply the changes");
      return null;
    }

    const updated = res.data;
    setRows((prev) => prev.map((item) => (item.order_id === row.order_id ? updated : item)));
    setOverrides((prev) => {
      const next = { ...prev };
      if (Object.keys(changed).length) next[row.order_id] = changed;
      else delete next[row.order_id];
      return next;
    });
    // An edit can make a selected row unusable again.
    if (updated.validation?.length) {
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(row.order_id);
        return next;
      });
    }
    return updated;
  };

  /** The derived row for an order, remembered the first time it is edited. */
  const baselineFor = (row) => {
    const known = baselines[row.order_id];
    if (known) return known;
    setBaselines((prev) => ({ ...prev, [row.order_id]: row }));
    return row;
  };

  /**
   * The customer name, edited straight in the grid. Committed on blur or Enter
   * rather than per keystroke, so one edit is one preview call.
   */
  const editCustomerName = async (row, value) => {
    const next = String(value ?? "").trim();
    if (next === String(row.customer_name ?? "")) return;

    const baseline = baselineFor(row);
    const changed = { ...(overrides[row.order_id] || {}) };
    // Back to what was derived means it is no longer an override at all.
    if (next === String(baseline.customer_name ?? "")) delete changed.customer_name;
    else changed.customer_name = next;

    setSavingCell(`${row.order_id}:customer_name`);
    try {
      const updated = await commitOverrides(row, changed);
      if (updated?.validation?.length) Toast("error", updated.validation.join(" • "));
    } catch (error) {
      console.error(error);
      Toast("error", "Could not apply the changes");
    }
    setSavingCell("");
  };

  /**
   * The invoice date, edited straight in the grid. The same `date` the dialog
   * writes, so the server moves the PO Date custom field with it either way.
   */
  const editDate = async (row, value) => {
    const next = String(value ?? "").trim();
    if (next === String(row.date ?? "")) return;
    // The picker cannot produce anything else, but a cleared field can - and
    // an invoice with no date is not one the server should be asked to price.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(next)) {
      Toast("error", "Invoice date must be a valid date");
      return;
    }

    const baseline = baselineFor(row);
    const changed = { ...(overrides[row.order_id] || {}) };
    if (next === String(baseline.date ?? "")) delete changed.date;
    else changed.date = next;

    setSavingCell(`${row.order_id}:date`);
    try {
      const updated = await commitOverrides(row, changed);
      if (updated?.validation?.length) Toast("error", updated.validation.join(" • "));
    } catch (error) {
      console.error(error);
      Toast("error", "Could not apply the changes");
    }
    setSavingCell("");
  };

  /**
   * The amount, edited straight in the grid. Writes the same
   * invoice_items[0].rate the dialog's "Amount (excl. GST)" writes, so the
   * server re-derives sub_total, tax_amount and total_amount from it either
   * way.
   */
  const editRate = async (row, value) => {
    const typed = String(value ?? "").trim();
    const amount = Number(typed);
    if (!typed || !Number.isFinite(amount) || amount <= 0) {
      Toast("error", "Amount must be a number above zero");
      return;
    }
    // Compared as numbers so "5000.00" is not treated as a change from 5000.
    if (amount === Number(row.invoice_items?.[0]?.rate)) return;

    const baseline = baselineFor(row);
    const changed = { ...(overrides[row.order_id] || {}) };
    if (amount === Number(baseline.invoice_items?.[0]?.rate)) delete changed.rate;
    else changed.rate = typed;

    setSavingCell(`${row.order_id}:rate`);
    try {
      const updated = await commitOverrides(row, changed);
      if (updated?.validation?.length) Toast("error", updated.validation.join(" • "));
    } catch (error) {
      console.error(error);
      Toast("error", "Could not apply the changes");
    }
    setSavingCell("");
  };

  const saveEdit = async () => {
    if (!editing || editEmailError) return;
    const { row, baseline, form } = editing;

    // Diffed against the derived baseline, not against `row`: `row` already
    // carries any earlier edit, so diffing against it would report "nothing
    // changed" on a second pass and drop the overrides already applied.
    const changed = EDIT_FIELDS.reduce((diff, field) => {
      if (field.readOnly) return diff;
      const original = readPath(baseline, field.path);
      const current = form[field.key] ?? "";
      if (String(original ?? "") !== current) diff[field.key] = current;
      return diff;
    }, {});

    setSavingEdit(true);
    try {
      const updated = await commitOverrides(row, changed);
      if (updated) {
        setEditing(null);
        Toast(
          updated.validation?.length ? "error" : "success",
          updated.validation?.length ? updated.validation.join(" • ") : "Row updated"
        );
      }
    } catch (error) {
      console.error(error);
      Toast("error", "Could not apply the changes");
    }
    setSavingEdit(false);
  };

  const resetEdit = (orderId) => {
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
    setEditing(null);
    // Re-fetches, which clears the baselines with everything else.
    fetchCandidates();
  };

  const handleGenerate = async () => {
    if (!selected.size) return;
    setGenerating(true);
    try {
      const res = await api({
        url: "/zoho-invoice/generate",
        method: "POST",
        responseType: "json",
        data: { order_ids: [...selected], end_before: endBefore, overrides },
      });
      if (res.code === 200) {
        const { created = 0, skipped = [], failed = [] } = res.data || {};
        Toast(
          created ? "success" : "error",
          `${created} invoice request${created === 1 ? "" : "s"} generated` +
            (skipped.length ? `, ${skipped.length} skipped` : "") +
            (failed.length ? `, ${failed.length} failed` : "")
        );
        [...skipped, ...failed].forEach((item) =>
          console.warn(`${item.order_id}: ${item.reason}`)
        );
        fetchCandidates();
      } else {
        Toast("error", res.message || "Could not generate invoice requests");
      }
    } catch (error) {
      console.error(error);
      Toast("error", "Could not generate invoice requests");
    }
    setGenerating(false);
  };

  useEffect(() => {
    try {
      localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify(hiddenColumns));
    } catch (error) {
      // Private browsing, or a full quota - the picker still works this session.
      console.warn("Could not save column preferences", error);
    }
  }, [hiddenColumns]);

  const isRaised = view === "raised";
  const viewKey = isRaised ? "raised" : "candidates";

  // Unknown ids are treated as visible, so a column added later shows up
  // without anyone having to touch their saved preferences.
  const shown = useCallback(
    (columns) => columns.filter((col) => !hiddenColumns[viewKey].includes(col.id)),
    [hiddenColumns, viewKey]
  );
  const panelCols = useMemo(() => shown(PANEL_COLUMNS), [shown]);
  const zohoCols = useMemo(() => shown(ZOHO_COLUMNS), [shown]);
  const itemCols = useMemo(() => shown(ITEM_COLUMNS), [shown]);
  const raisedCols = useMemo(() => shown(RAISED_COLUMNS), [shown]);

  const toggleColumn = (id) =>
    setHiddenColumns((prev) => {
      const list = prev[viewKey];
      return {
        ...prev,
        [viewKey]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id],
      };
    });

  const setGroupVisible = (columns, visible) =>
    setHiddenColumns((prev) => {
      const ids = columns.map((col) => col.id);
      const rest = prev[viewKey].filter((x) => !ids.includes(x));
      return { ...prev, [viewKey]: visible ? rest : [...rest, ...ids] };
    });

  const hiddenCount = hiddenColumns[viewKey].length;

  /**
   * Downloads what the grid is showing - every row the current filters match,
   * not just the page on screen, and only the columns left visible in the
   * picker, in the order they appear.
   *
   * The rows come back through the same endpoints the grid uses, a page at a
   * time, so an export can never disagree with what finance is looking at. The
   * values run through the columns' own render functions for the same reason:
   * a date reads as a date in the file exactly as it does on screen.
   */
  const exportCsv = async () => {
    setExporting(true);
    try {
      // One request for the whole filtered set. Walking pages could not work
      // here anyway: candidates are filtered after each page is read - zero
      // spend, already fully invoiced - so a page comes back short without
      // meaning the end was reached, and the loop had to guess.
      const res = await api({
        url: isRaised ? "/zoho-invoice/requests" : "/zoho-invoice/candidates",
        method: "POST",
        responseType: "json",
        data: isRaised
          ? { status: statusFilter === "-1" ? null : Number(statusFilter), search, all: true }
          : { end_before: endBefore, search, all: true },
      });
      if (res.code !== 200) throw new Error(res.message || "Request failed");

      const collected = res.data?.data || [];
      if (!collected.length) {
        Toast("error", "Nothing to download for these filters");
        return;
      }

      const columns = isRaised
        ? [{ label: "Status", render: (row) => (REQUEST_STATUS[row.status] || {}).label || row.status },
           ...raisedCols.map((col) => ({ label: col.label, render: (row) => cellValue(row, col) }))]
        : [
            ...panelCols.map((col) => ({ label: col.label, render: (row) => cellValue(row, col) })),
            ...zohoCols.map((col) => ({ label: col.label, render: (row) => cellValue(row, col) })),
            // One line item per invoice today, but joined rather than assumed:
            // a second one would otherwise vanish from the file silently.
            ...itemCols.map((col) => ({
              label: `item.${col.label}`,
              render: (row) =>
                (row.invoice_items || []).map((item) => cellValue(item, col) ?? "").join(" | "),
            })),
            // The blocked/warning icon the grid shows, spelled out - this is
            // the column finance works from when chasing the data fixes.
            { label: "Issues", render: (row) => (row.validation || []).join("; ") },
            { label: "Warnings", render: (row) => (row.warnings || []).join("; ") },
          ];

      const csv = toCsv(
        columns.map((col) => col.label),
        collected.map((row) => columns.map((col) => col.render(row) ?? ""))
      );
      downloadCsv(`zoho-${isRaised ? "raised-invoices" : "to-invoice"}-${todayInput()}.csv`, csv);
      Toast("success", `${collected.length} row${collected.length === 1 ? "" : "s"} downloaded`);
    } catch (error) {
      console.error(error);
      Toast("error", "Could not download the CSV");
    }
    setExporting(false);
  };

  const totalPages = Math.ceil((isRaised ? requestsTotal : total) / PAGE_SIZE);
  const currentPage = isRaised ? requestsPage : page;
  const setCurrentPage = isRaised ? setRequestsPage : setPage;

  return (
    <SlDrawer
      label="Generate Zoho Invoices"
      className="gi-drawer"
      open={props.show}
      onSlRequestClose={handleRequestClose}
      onSlAfterHide={handleAfterHide}
      style={{ "--size": "95%" }}
    >
      <div className="d-flex items-center gap-2 mb-3">
        <SlButton
          size="small"
          variant={view === "candidates" ? "primary" : "default"}
          onClick={() => setView("candidates")}
        >
          To Invoice
        </SlButton>
        <SlButton
          size="small"
          variant={view === "raised" ? "primary" : "default"}
          onClick={() => setView("raised")}
        >
          Raised
        </SlButton>
      </div>

      {view === "candidates" ? (
        <div className="d-flex items-end gap-3 flex-wrap mb-3">
          <SlInput
            type="date"
            size="small"
            label="Campaigns started before"
            value={endBefore}
            onSlChange={(e) => {
              setPage(0);
              setEndBefore(e.target.value);
            }}
          ></SlInput>
          <SlButton size="small" onClick={fetchCandidates} disabled={loader}>
            <SlIcon slot="prefix" library="lucide" name="refresh-cw"></SlIcon>
            Refresh
          </SlButton>
          <SlButton size="small" onClick={() => setPickingColumns(true)}>
            <SlIcon slot="prefix" library="lucide" name="columns-3"></SlIcon>
            Columns{hiddenCount ? ` (${hiddenCount} hidden)` : ""}
          </SlButton>
          <SlButton size="small" onClick={exportCsv} loading={exporting} disabled={loader}>
            <SlIcon slot="prefix" library="lucide" name="download"></SlIcon>
            CSV
          </SlButton>
          <SlInput
            size="small"
            clearable
            placeholder="Order, customer, brand, salesperson"
            value={searchInput}
            onSlInput={(e) => setSearchInput(e.target.value)}
          >
            <SlIcon slot="prefix" library="lucide" name="search"></SlIcon>
          </SlInput>
          <div className="ml-auto d-flex items-center gap-3">
            <span className="text-sm text-gray-600">
              {selected.size} of {validRows.length} selectable / {total} with spend left to
              bill
            </span>
            {noSpend ? (
              <SlTooltip
                hoist
                content="Orders that ended without delivering anything - nothing to bill, so they are not listed"
              >
                <span className="rounded bg-slate-100 px-2 py-1 text-sm text-slate-600">
                  {noSpend} skipped (no spend)
                </span>
              </SlTooltip>
            ) : null}
            <SlButton
              variant="primary"
              size="small"
              disabled={!selected.size || generating}
              loading={generating}
              onClick={handleGenerate}
            >
              Generate
            </SlButton>
          </div>
        </div>
      ) : (
        <div className="d-flex items-end gap-3 flex-wrap mb-3">
          <SlSelect
            size="small"
            label="Status"
            value={statusFilter}
            style={{ minWidth: "10rem" }}
            onSlChange={(e) => {
              setRequestsPage(0);
              setStatusFilter(e.target.value);
            }}
          >
            <SlOption value="-1">All</SlOption>
            {Object.entries(REQUEST_STATUS).map(([value, status]) => (
              <SlOption key={value} value={value}>
                {status.label}
              </SlOption>
            ))}
          </SlSelect>
          <SlButton size="small" onClick={fetchRequests} disabled={loader}>
            <SlIcon slot="prefix" library="lucide" name="refresh-cw"></SlIcon>
            Refresh
          </SlButton>
          <SlButton size="small" onClick={() => setPickingColumns(true)}>
            <SlIcon slot="prefix" library="lucide" name="columns-3"></SlIcon>
            Columns{hiddenCount ? ` (${hiddenCount} hidden)` : ""}
          </SlButton>
          <SlButton size="small" onClick={exportCsv} loading={exporting} disabled={loader}>
            <SlIcon slot="prefix" library="lucide" name="download"></SlIcon>
            CSV
          </SlButton>
          <SlInput
            size="small"
            clearable
            placeholder="Order, customer, brand, salesperson"
            value={searchInput}
            onSlInput={(e) => setSearchInput(e.target.value)}
          >
            <SlIcon slot="prefix" library="lucide" name="search"></SlIcon>
          </SlInput>
          <span className="ml-auto text-sm text-gray-600">
            {requestsTotal} invoice requests
          </span>
        </div>
      )}

      {loader ? (
        <TableLoader />
      ) : view === "raised" ? (
        <ScrollContainer className="gi-scroll">
          <table className="table table-bordered mt-2 whitespace-nowrap gi-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Status</th>
                {raisedCols.map((col) => (
                  <th key={col.label}>{col.label}</th>
                ))}
                <th>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {requests.length ? (
                requests.map((row, index) => {
                  const status = REQUEST_STATUS[row.status] || { label: row.status, variant: "neutral" };
                  return (
                    <tr key={row.id} className={row.status === 2 ? "bg-red-50" : ""}>
                      <td>{requestsPage * PAGE_SIZE + index + 1}</td>
                      <td>
                        <span className="d-flex items-center gap-1">
                          <SlBadge variant={status.variant} pill>
                            {status.label}
                          </SlBadge>
                          {row.zoho_error ? (
                            <SlTooltip hoist content={row.zoho_error}>
                              <SlIcon
                                library="lucide"
                                name="alert-triangle"
                                className="text-red-500"
                              ></SlIcon>
                            </SlTooltip>
                          ) : null}
                        </span>
                      </td>
                      {raisedCols.map((col) => {
                        const value = cellValue(row, col);
                        return (
                          <td key={col.label}>
                            {value === null ? (
                              <span className="text-gray-400 italic">{col.empty || "-"}</span>
                            ) : (
                              value
                            )}
                          </td>
                        );
                      })}
                      <td>
                        {row.status === 1 ? (
                          <SlButton
                            size="small"
                            loading={pdfLoading === row.id}
                            onClick={() => openPdf(row)}
                          >
                            <SlIcon slot="prefix" library="lucide" name="file-text"></SlIcon>
                            PDF
                          </SlButton>
                        ) : (
                          <span className="text-gray-400 italic">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={raisedCols.length + 3}>
                    <p className="text-center my-3">No invoice requests to show.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </ScrollContainer>
      ) : (
        <ScrollContainer className="gi-scroll">
          <table className="table table-bordered mt-2 whitespace-nowrap gi-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={allValidSelected}
                    disabled={!validRows.length}
                    onChange={toggleAll}
                  />
                </th>
                <th>S.No</th>
                <th>Edit</th>
                {panelCols.map((col) => (
                  <th key={col.label} className="bg-slate-50">
                    {col.label}
                  </th>
                ))}
                {zohoCols.map((col) => (
                  <th key={col.label}>{col.label}</th>
                ))}
                {itemCols.map((col) => (
                  <th key={col.label} className="bg-amber-50">
                    invoice_items[].{col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length ? (
                rows.map((row, index) => {
                  const blocked = !!row.validation?.length;
                  // Raisable, but something is thin - flagged beside the
                  // checkbox instead of taking the row out of the selection.
                  const warned = !blocked && !!row.warnings?.length;
                  const edited = !!overrides[row.order_id];
                  // The reason tooltip is hoisted: it renders inside
                  // ScrollContainer's overflow, which clips it otherwise.
                  return (
                    <tr
                      key={row.order_id}
                      className={blocked ? "bg-red-50 text-gray-500" : ""}
                    >
                      <td>
                        {blocked ? (
                          <SlTooltip hoist content={row.validation.join(" • ")}>
                            <SlIcon
                              library="lucide"
                              name="alert-triangle"
                              className="text-red-500"
                            ></SlIcon>
                          </SlTooltip>
                        ) : (
                          <span className="d-flex items-center gap-1">
                            <input
                              type="checkbox"
                              aria-label={`Select ${row.order_id}`}
                              checked={selected.has(row.order_id)}
                              onChange={() => toggleRow(row.order_id)}
                            />
                            {warned ? (
                              <SlTooltip hoist content={row.warnings.join(" • ")}>
                                <SlIcon
                                  library="lucide"
                                  name="info"
                                  className="text-amber-600"
                                ></SlIcon>
                              </SlTooltip>
                            ) : null}
                          </span>
                        )}
                      </td>
                      <td>{page * PAGE_SIZE + index + 1}</td>
                      <td>
                        <div className="d-flex items-center gap-1">
                          <sl-tooltip content="Correct this row before raising it">
                            <sl-icon-button
                              library="lucide"
                              name="pencil"
                              onClick={() => openEdit(row)}
                            ></sl-icon-button>
                          </sl-tooltip>
                          {edited ? (
                            <SlTooltip
                              hoist
                              content={`Edited: ${Object.keys(overrides[row.order_id]).join(", ")}`}
                            >
                              <SlBadge variant="primary" pill>
                                edited
                              </SlBadge>
                            </SlTooltip>
                          ) : null}
                        </div>
                      </td>
                      {panelCols.map((col) => (
                        <td key={col.label} className="bg-slate-50">
                          {col.edit ? (
                            <SlInput
                              size="small"
                              className="gi-inline-input"
                              value={row[col.key] || ""}
                              disabled={savingCell === `${row.order_id}:${col.key}`}
                              // On blur or Enter, not per keystroke: one edit
                              // is one preview call.
                              onSlChange={(e) => editCustomerName(row, e.target.value)}
                            ></SlInput>
                          ) : (
                            cellValue(row, col) ?? "-"
                          )}
                        </td>
                      ))}
                      {zohoCols.map((col) => {
                        const value = cellValue(row, col);
                        return (
                          <td key={col.label}>
                            {col.edit ? (
                              <SlInput
                                size="small"
                                type="date"
                                className="gi-inline-date"
                                value={row[col.key] || ""}
                                disabled={savingCell === `${row.order_id}:${col.key}`}
                                // Committed on change, like the customer name
                                // and the amount, so one edit is one preview.
                                onSlChange={(e) => editDate(row, e.target.value)}
                              ></SlInput>
                            ) : value === null ? (
                              <span className="text-gray-400 italic">{col.empty || "-"}</span>
                            ) : (
                              value
                            )}
                          </td>
                        );
                      })}
                      {itemCols.map((col) => (
                        <td key={col.label} className="bg-amber-50">
                          {col.edit ? (
                            <SlInput
                              size="small"
                              type="number"
                              className="gi-inline-number"
                              value={row.invoice_items?.[0]?.rate ?? ""}
                              disabled={savingCell === `${row.order_id}:${col.key}`}
                              // Committed on blur or Enter, like the customer
                              // name, so one edit is one preview call.
                              onSlChange={(e) => editRate(row, e.target.value)}
                            ></SlInput>
                          ) : (
                          (row.invoice_items || []).map((item, i) => {
                            const value = cellValue(item, col);
                            return (
                              <p key={i} className="my-0">
                                {value === null ? (
                                  <span className="text-gray-400 italic">
                                    {col.empty || "-"}
                                  </span>
                                ) : (
                                  value
                                )}
                              </p>
                            );
                          })
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={3 + panelCols.length + zohoCols.length + itemCols.length}>
                    <p className="text-center my-3">
                      No orders waiting to be invoiced for this date.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </ScrollContainer>
      )}

      {totalPages > 1 && (
        <div className="d-flex justify-content-center items-center gap-3 mt-3">
          <SlButton
            size="small"
            disabled={currentPage === 0}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            Prev
          </SlButton>
          <span className="text-sm">
            Page {currentPage + 1} of {totalPages}
          </span>
          <SlButton
            size="small"
            disabled={currentPage + 1 >= totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next
          </SlButton>
        </div>
      )}

      <SlDialog
        label={`Invoice ${pdf?.label || ""}`}
        open={!!pdf}
        style={{ "--width": "60rem" }}
        onSlAfterHide={(event) => {
          // Same bubbling guard as the drawer: only the dialog's own event.
          if (event.target !== event.currentTarget) return;
          closePdf();
        }}
      >
        {pdf ? (
          // <embed>, matching the Orders page viewer: a document load, so a
          // file served from S3 is not held to CORS the way an XHR for it is.
          <embed
            title={`Invoice ${pdf.label}`}
            src={pdf.url}
            type="application/pdf"
            style={{ width: "100%", height: "75vh", border: "none" }}
          />
        ) : null}
        <SlButton slot="footer" variant="primary" onClick={closePdf}>
          Close
        </SlButton>
      </SlDialog>

      <SlDialog
        label="Choose columns"
        open={pickingColumns}
        style={{ "--width": "40rem" }}
        onSlAfterHide={(event) => {
          // Same bubbling guard as the drawer: only the dialog's own event.
          if (event.target !== event.currentTarget) return;
          setPickingColumns(false);
        }}
      >
        <p className="text-sm text-gray-500 mt-0">
          Hidden columns are remembered on this browser, separately for this tab
          and the other one.
        </p>
        {COLUMN_GROUPS[viewKey].map((group) => (
          <div key={group.title} className="mb-4">
            <div className="d-flex items-center gap-3 mb-2">
              <strong className="text-sm">{group.title}</strong>
              <SlButton size="small" variant="text" onClick={() => setGroupVisible(group.columns, true)}>
                All
              </SlButton>
              <SlButton size="small" variant="text" onClick={() => setGroupVisible(group.columns, false)}>
                None
              </SlButton>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {group.columns.map((col) => (
                <label key={col.id} className="d-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!hiddenColumns[viewKey].includes(col.id)}
                    onChange={() => toggleColumn(col.id)}
                  />
                  {col.label}
                </label>
              ))}
            </div>
          </div>
        ))}
        <SlButton
          slot="footer"
          onClick={() => setHiddenColumns((prev) => ({ ...prev, [viewKey]: [] }))}
        >
          Show all
        </SlButton>
        <SlButton slot="footer" variant="primary" onClick={() => setPickingColumns(false)}>
          Done
        </SlButton>
      </SlDialog>

      <SlDialog
        label={editing ? `Edit ${editing.row.order_id}` : "Edit"}
        open={!!editing}
        style={{ "--width": "46rem" }}
        onSlAfterHide={(event) => {
          // Same bubbling guard as the drawer: only the dialog's own event.
          if (event.target !== event.currentTarget) return;
          setEditing(null);
        }}
      >
        {editing ? (
          <>
            <p className="text-sm text-gray-500 mt-0">
              Changes are re-checked on the server and stored with the request, so
              it stays visible that this invoice was corrected by hand.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {EDIT_FIELDS.map((field) => {
                const value = editing.form[field.key] ?? "";
                const choices = field.options ? options[field.options] || [] : null;
                return (
                  <div key={field.key} className={field.key === "description" ? "col-span-2" : ""}>
                    {choices ? (
                      <SlSelect
                        size="small"
                        label={field.label}
                        value={value}
                        onSlChange={(e) => setEditField(field.key, e.target.value)}
                      >
                        {choices.map((choice) => {
                          const val = choice.code ?? choice;
                          const text = choice.name ? `${choice.name} (${choice.code})` : choice;
                          return (
                            <SlOption key={val} value={val}>
                              {text}
                            </SlOption>
                          );
                        })}
                      </SlSelect>
                    ) : (
                      <SlInput
                        size="small"
                        type={field.type || "text"}
                        label={field.label}
                        value={value}
                        disabled={field.readOnly}
                        onSlInput={(e) => setEditField(field.key, e.target.value)}
                      >
                        {field.key === "email" && editEmailError ? (
                          <span slot="help-text" className="text-red-600">
                            {editEmailError}
                          </span>
                        ) : field.hint ? (
                          <span slot="help-text">{field.hint}</span>
                        ) : null}
                      </SlInput>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
        {editing && overrides[editing.row.order_id] ? (
          <SlButton slot="footer" onClick={() => resetEdit(editing.row.order_id)}>
            Reset to derived
          </SlButton>
        ) : null}
        <SlButton slot="footer" onClick={() => setEditing(null)}>
          Cancel
        </SlButton>
        <SlButton
          slot="footer"
          variant="primary"
          loading={savingEdit}
          disabled={!!editEmailError}
          onClick={saveEdit}
        >
          Apply
        </SlButton>
      </SlDialog>
    </SlDrawer>
  );
}

export default GenerateInvoices;
